import { FunctionComponent } from 'react';
import './styles/DanceSchoolSwitcherModal.scss';

import { Divider, Stack, Typography } from '@mui/material';
import { Add, ArrowForward } from '@mui/icons-material';

import { Avatar, Modal, ProgressSpinner } from '@dw/components/core';
import { useGetMyDanceSchools } from '@dw/hooks/api/DanceSchoolApi';
import { IDanceSchool, getSchoolMarkKey } from '@dw/models/danceSchool';
import { componentClassName } from '@dw/utils';
import { PendingInviteList } from './PendingInviteList';
import { useAuth, useBinaryToggle, useSnackbar } from '@dw/hooks';
import { useSwitchSchoolsMutation } from '@dw/hooks/api/AuthApi';
import { useGetMyPendingDanceSchoolInvites } from '@dw/hooks/api/DanceSchoolUserApi';

import { CreateNewDanceSchoolModal } from './CreateNewDanceSchoolModal';

interface IDanceSchoolSwitcherModalProps {
  open: boolean;
  onClose: () => void;
}
export const DanceSchoolSwitcherModal: FunctionComponent<IDanceSchoolSwitcherModalProps> = (props: IDanceSchoolSwitcherModalProps) => {

  const {
    open,
    onClose
  } = props;

  const { danceSchool } = useAuth();
  const { showSnackbar } = useSnackbar();

  const { isOpen: createDanceSchoolOpen, toggle: toggleCreateDanceSchoolOpen } = useBinaryToggle();

  const { data: myDanceSchools } = useGetMyDanceSchools();
  const { data } = useGetMyPendingDanceSchoolInvites();
  const pendingInvites = data ?? [];

  const { mutate: switchSchools, isPending: switchSchoolsLoading, variables: switchSchoolsVariables } = useSwitchSchoolsMutation({
    onSuccess: () => {
      onClose();
    }
  });

  const handleSwitchSchools = (school: IDanceSchool) => {
    if (danceSchool?.id === school.id) {
      onClose();
      return;
    }

    switchSchools({
      danceSchoolId: school.id
    });
  }

  const cn = componentClassName('school-switcher-modal');

  const handleCreateNewDanceSchool = () => {
    onClose();
    toggleCreateDanceSchoolOpen();
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title='Switch Dance Schools'
      >
        {!myDanceSchools ? (
          <Stack justifyContent='center' alignItems='center' padding={5}>
            <ProgressSpinner />
          </Stack>
        ) : (
          <Stack className={cn()}>
            {myDanceSchools.map(school => (
              <Stack
                key={school.id}
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                className='school-option pointer hover-background'
                onClick={() => handleSwitchSchools(school)}
              >
                <Stack direction='row' alignItems='center' columnGap={1}>
                  <Avatar imageKey={getSchoolMarkKey(school)} size='tiny' shape='square' />
                  <Typography variant='body2'>{school.name}</Typography>
                </Stack>

                {danceSchool?.id === school.id ? (
                  <Typography variant='body2' fontWeight={600} color='primary'>Current</Typography>
                ) : switchSchoolsLoading && switchSchoolsVariables?.danceSchoolId === school.id ? (
                  <ProgressSpinner size={20} />
                ) : (
                  <ArrowForward />
                )}
              </Stack>
            ))}

            {/*
              Invitations sit between the schools you are already in and the option to start a new
              one, because that is what they are: a school you are one click away from being in.
              They are deliberately not rows that switch on click - the whole row is not the action,
              the Accept button is, and a stray click must not join somebody to a school.
            */}
            {pendingInvites.length > 0 && (
              <>
                <Divider />

                <Stack className={cn('invites')} rowGap={0.5}>
                  <Typography variant='caption' className={cn('invites-label')}>
                    {pendingInvites.length === 1 ? 'You have been invited to' : 'You have been invited to these schools'}
                  </Typography>

                  {/*
                    Accepting invalidates the school list and the invites, so the school moves up
                    into the list above on its own. Switching to it stays a separate, deliberate
                    click - the user is looking at the switcher, and joining a school is not the
                    same as going there. The login screen, which has no school to stay on, does
                    switch.
                  */}
                  <PendingInviteList
                    invites={pendingInvites}
                    onAccepted={(invite) => showSnackbar(`You have joined ${invite.danceSchool?.name ?? 'the school'}.`)}
                  />
                </Stack>

                <Divider />
              </>
            )}

            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              className='school-option pointer hover-background'
              onClick={handleCreateNewDanceSchool}
            >
              <Typography variant='body1'>Create New School</Typography>

              <Add />
            </Stack>
          </Stack>
        )}
      </Modal>
    
      <CreateNewDanceSchoolModal
        open={createDanceSchoolOpen}
        onClose={toggleCreateDanceSchoolOpen}
      />
    </>
  )
}